import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { Button } from '@uhg-abyss/web/ui/Button';
import { SpecType, LLMSettings, LLMJudgeMetrics, DEFAULT_LLM_SETTINGS } from '../types';
import { Theme } from '../styles/theme';
import { LLMSettingsPanel } from './LLMSettingsPanel';
import { LLMJudgeRibbon } from './LLMJudgeRibbon';
import { ButtonRow, GuardrailAlertBlock } from './GuardrailAlertBlock';
import { getFileExtension, HPS_FILE_TYPES } from '../constants/hpsFileTypes';

const UploadContainer = styled.div<{ colors: Theme['colors'] }>`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2<{ color: string }>`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color};
`;

const Select = styled.select<{ borderColor: string; focusColor: string }>`
  padding: 12px 16px;
  border: 2px solid ${props => props.borderColor};
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.focusColor};
    box-shadow: 0 0 0 3px ${props => props.focusColor}20;
  }
`;

const DropZone = styled.div<{ isDragging: boolean; accentColor: string; borderColor: string }>`
  border: 2px dashed ${props => props.isDragging ? props.accentColor : props.borderColor};
  border-radius: 12px;
  padding: 48px 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.isDragging ? `${props.accentColor}10` : 'transparent'};
  
  &:hover {
    border-color: ${props => props.accentColor};
    background: ${props => props.accentColor}05;
  }
`;

const UploadIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: ${props => props.color}15;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadText = styled.p<{ color: string }>`
  font-size: 16px;
  color: ${props => props.color};
  margin-bottom: 8px;
`;

const UploadHint = styled.p<{ color: string }>`
  font-size: 14px;
  color: ${props => props.color};
`;

const SelectedFile = styled.div<{ bgColor: string; textColor: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.bgColor};
  border-radius: 8px;
  margin-top: 16px;
`;

const FileName = styled.span<{ color: string }>`
  font-weight: 500;
  color: ${props => props.color};
`;

const FileSize = styled.span<{ color: string }>`
  font-size: 14px;
  color: ${props => props.color};
`;

const ValidateButton = styled.button<{ bgColor: string; hoverColor: string }>`
  width: 100%;
  padding: 16px 32px;
  background: linear-gradient(135deg, ${props => props.bgColor} 0%, ${props => props.hoverColor} 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px ${props => props.bgColor}40;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const UploadErrorModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.5);
`;

const UploadErrorCard = styled.div`
  width: min(480px, 100%);
  padding: 28px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
`;

const UploadErrorTitle = styled.h3`
  margin: 0 0 12px;
  color: #a8071a;
`;

const UploadErrorText = styled.p`
  margin: 0 0 20px;
  line-height: 1.5;
`;



interface FileUploadProps {
  specType: SpecType;
  onSpecChange: (spec: SpecType) => void;
  onFileSelect: (file: File) => void;
  onValidate: () => void;
  selectedFile: File | null;
  isValidating: boolean;
  llmSettings?: LLMSettings;
  onSettingsChange?: (settings: LLMSettings) => void;
  judgeMetrics?: LLMJudgeMetrics | null;
  judgeLoading?: boolean;
  guardrailError?: { category: string; message: string } | null;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  specType,
  onSpecChange,
  onFileSelect,
  onValidate,
  selectedFile,
  isValidating,
  llmSettings = DEFAULT_LLM_SETTINGS,
  onSettingsChange,
  judgeMetrics,
  judgeLoading,
  guardrailError,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const expectedFileType = HPS_FILE_TYPES[specType];

  const selectFile = (file: File) => {
    const extension = getFileExtension(file.name);
    if (extension !== expectedFileType.extension) {
      setUploadError(`Only ${expectedFileType.extension} files can be uploaded for ${expectedFileType.label}. Please select a file with the required extension.`);
      return;
    }
    onFileSelect(file);
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) selectFile(file);
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) selectFile(file);
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  return (
    <UploadContainer colors={theme.colors}>
      <SectionTitle color={theme.colors.optumBlue}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill={theme.colors.optumOrange}>
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
        Upload File for Validation
      </SectionTitle>
      
      <FormRow>
        <FormGroup>
          <Label color={theme.colors.textSecondary}>Select File Specification</Label>
          <Select
            value={specType}
            onChange={(e) => onSpecChange(e.target.value as SpecType)}
            borderColor={theme.colors.border}
            focusColor={theme.colors.optumBlue}
          >
            <option value="claimstatus30">Claim Status 3.0 (.cstat)</option>
            <option value="eligibility31">Eligibility 3.1 (.elig31)</option>
            <option value="eligibility30">Eligibility 3.0 (.elig30)</option>
            <option value="individualusagebenefit30">Individual Usage Benefit 3.0 (.indi30)</option>
            <option value="planbenefit30">Plan Benefit 3.0 (.plan30)</option>
            <option value="standardbenefitentity30">Standard Benefit Entity 3.0 (.bene30)</option>
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label color={theme.colors.textSecondary}>Upload Data File</Label>
          <DropZone
            isDragging={isDragging}
            accentColor={theme.colors.optumBlue}
            borderColor={theme.colors.border}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={expectedFileType.extension}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <UploadIcon color={theme.colors.optumBlue}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill={theme.colors.optumBlue}>
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
              </svg>
            </UploadIcon>
            <UploadText color={theme.colors.textPrimary}>
              Drag and drop your file here, or click to browse
            </UploadText>
            <UploadHint color={theme.colors.textSecondary}>
              Only {expectedFileType.extension} files are accepted for the selected {expectedFileType.label} specification.
            </UploadHint>
          </DropZone>
          
          {selectedFile && (
            <SelectedFile bgColor={`${theme.colors.success}15`} textColor={theme.colors.success}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill={theme.colors.success}>
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
              </svg>
              <FileName color={theme.colors.textPrimary}>{selectedFile.name}</FileName>
              <FileSize color={theme.colors.textSecondary}>
                ({formatFileSize(selectedFile.size)})
              </FileSize>
            </SelectedFile>
          )}
        </FormGroup>
      </FormRow>
      
      <ButtonRow>
        <ValidateButton
          onClick={onValidate}
          disabled={!selectedFile || isValidating}
          bgColor={theme.colors.optumBlue}
          hoverColor={theme.colors.accentBlue}
        >
          {isValidating ? 'Validating...' : 'Validate File'}
        </ValidateButton>
        {onSettingsChange && (
          <LLMSettingsPanel settings={llmSettings} onChange={onSettingsChange} />
        )}
      </ButtonRow>

      {guardrailError && (
        <GuardrailAlertBlock error={guardrailError} />
      )}

      <LLMJudgeRibbon
        metrics={judgeMetrics ?? null}
        loading={judgeLoading}
        label="Validation Quality"
      />

      {uploadError && (
        <UploadErrorModal role="alertdialog" aria-modal="true" aria-labelledby="validator-upload-error-title">
          <UploadErrorCard>
            <UploadErrorTitle id="validator-upload-error-title">Unsupported file format</UploadErrorTitle>
            <UploadErrorText>{uploadError}</UploadErrorText>
            <Button variant="filled" onClick={() => setUploadError(null)}>OK</Button>
          </UploadErrorCard>
        </UploadErrorModal>
      )}
    </UploadContainer>
  );
};
